import type {
  Task,
  Agency,
  Developer,
  ActivityLogEntry,
  FileAsset,
  Comment,
  DashboardSettings,
  ProjectNotification,
  AuthUser,
  PasswordResetRequest,
} from '@/types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';
const API_TOKEN = import.meta.env.VITE_API_TOKEN;
const TOKEN_STORAGE_KEY = 'orbitcrew_session_token';

let sessionToken = localStorage.getItem(TOKEN_STORAGE_KEY) || '';

export function setSessionToken(token: string): void {
  sessionToken = token;
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function clearSessionToken(): void {
  sessionToken = '';
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

export function getSessionToken(): string {
  return sessionToken;
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_URL}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
      ...(!sessionToken && API_TOKEN ? { Authorization: `Bearer ${API_TOKEN}` } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// Tasks API
export const tasksApi = {
  getAll: () => fetchApi<Task[]>('/tasks'),
  
  getById: (id: string) => fetchApi<Task>(`/tasks/${id}`),
  
  create: (data: {
    title: string;
    description: string;
    requestType: string;
    priority: string;
    agencyId?: string;
  }) => fetchApi<Task>('/tasks', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateStatus: (id: string, status: string) =>
    fetchApi<Task>(`/tasks/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  update: (id: string, data: {
    title?: string;
    description?: string;
    priority?: string;
    estimatedCompletion?: string;
    developerId?: string | null;
  }) => fetchApi<Task>(`/tasks/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),

  delete: (id: string) =>
    fetchApi<{ ok: boolean }>(`/tasks/${id}`, { method: 'DELETE' }),
  
  addComment: (id: string, data: {
    content: string;
    author?: string;
    isRevision?: boolean;
  }) => fetchApi<Comment>(`/tasks/${id}/comments`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  approve: (id: string, author?: string) => 
    fetchApi<Task>(`/tasks/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ author }),
    }),
  
  requestRevision: (id: string, data: {
    content: string;
    author?: string;
  }) => fetchApi<Task>(`/tasks/${id}/revision`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Agency API
export const agencyApi = {
  get: () => fetchApi<Agency>('/agency'),

  update: (data: Partial<{
    name: string;
    packageType: string;
    monthlyHours: number;
    extraHourRate: number;
    renewalDate: string;
    rolloverHours: number;
    hoursUsed: number;
  }>) => fetchApi<Agency>('/agency', { method: 'PATCH', body: JSON.stringify(data) }),

  updateHours: (hoursUsed: number) =>
    fetchApi<Agency>('/agency/hours', {
      method: 'PATCH',
      body: JSON.stringify({ hoursUsed }),
    }),

  getWeeklyWork: () => fetchApi<WeeklyWorkItem[]>('/agency/weekly-work'),

  getSprintProgress: () => fetchApi<SprintProgress>('/agency/sprint-progress'),
};

// Files API
export const filesApi = {
  getAll: () => fetchApi<FileAsset[]>('/files'),
  
  getByTaskId: (taskId: string) => fetchApi<FileAsset[]>(`/files/task/${taskId}`),
  
  upload: (data: {
    name: string;
    type: string;
    url?: string;
    size?: string;
    taskId?: string;
    uploadedBy?: string;
    contentBase64?: string;
    contentType?: string;
  }) => fetchApi<FileAsset>('/files', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  download: async (id: string) => {
    const response = await fetch(`${API_URL}/files/${id}/download`, {
      headers: {
        ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unable to download file' }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    return response.blob();
  },
  
  delete: (id: string) => 
    fetchApi<void>(`/files/${id}`, {
      method: 'DELETE',
    }),
};

// Activity API
export const activityApi = {
  getAll: () => fetchApi<ActivityLogEntry[]>('/activity'),
  
  getByTaskId: (taskId: string) => fetchApi<ActivityLogEntry[]>(`/activity/task/${taskId}`),
};

// Notifications API
export const notificationsApi = {
  getAll: () => fetchApi<ProjectNotification[]>('/notifications'),

  create: (data: { title: string; message: string }) =>
    fetchApi<ProjectNotification>('/notifications', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  markRead: (id: string) =>
    fetchApi<ProjectNotification>(`/notifications/${id}/read`, {
      method: 'PATCH',
    }),

  markAllRead: () =>
    fetchApi<ProjectNotification[]>('/notifications/read-all', {
      method: 'PATCH',
    }),
};

// Settings API
export const settingsApi = {
  get: () => fetchApi<DashboardSettings>('/settings'),

  update: (data: Partial<Omit<DashboardSettings, 'agencyId'>>) =>
    fetchApi<DashboardSettings>('/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
};

// Health check
export const healthApi = {
  check: () => fetchApi<{ status: string; timestamp: string; environment: string }>('/health'),
};

// Auth API
export const authApi = {
  register: (data: { name: string; email: string; password: string }) =>
    fetchApi<{ user: AuthUser; message: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  login: (data: { email: string; password: string }) =>
    fetchApi<{ token: string; user: AuthUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  me: () => fetchApi<AuthUser>('/auth/me'),

  logout: () =>
    fetchApi<{ ok: boolean }>('/auth/logout', {
      method: 'POST',
    }),

  forgotPassword: (email: string) =>
    fetchApi<{ message: string; requestId?: string; status?: PasswordResetRequest['status']; resetToken?: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (data: { resetToken: string; password: string }) =>
    fetchApi<{ ok: boolean }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  pendingUsers: () => fetchApi<AuthUser[]>('/auth/pending-users'),

  passwordResetRequests: () => fetchApi<PasswordResetRequest[]>('/auth/password-reset-requests'),

  approvePasswordResetRequest: (id: string) =>
    fetchApi<PasswordResetRequest>(`/auth/password-reset-requests/${id}/approve`, {
      method: 'POST',
    }),

  approveUser: (id: string) =>
    fetchApi<AuthUser>(`/auth/users/${id}/approve`, { method: 'POST' }),

  listUsers: () => fetchApi<AuthUser[]>('/auth/users'),

  updateUserRole: (id: string, role: 'admin' | 'client') =>
    fetchApi<AuthUser>(`/auth/users/${id}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),

  deleteUser: (id: string) =>
    fetchApi<{ ok: boolean }>(`/auth/users/${id}`, { method: 'DELETE' }),
};

// Developers API
export const developersApi = {
  getAll: () => fetchApi<Developer[]>('/developers'),

  getById: (id: string) => fetchApi<Developer>(`/developers/${id}`),

  create: (data: {
    name: string;
    role: string;
    avatar?: string;
    workingHours?: string;
    timezone?: string;
    skills?: string[];
    email?: string;
  }) => fetchApi<Developer>('/developers', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<{
    name: string;
    role: string;
    avatar: string;
    workingHours: string;
    timezone: string;
    skills: string[];
    email: string;
  }>) => fetchApi<Developer>(`/developers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string) =>
    fetchApi<{ ok: boolean }>(`/developers/${id}`, { method: 'DELETE' }),
};

export interface WeeklyWorkItem {
  day: string;
  tasks: string[];
  developer: string;
}

export interface SprintProgress {
  name: string;
  total: number;
  completed: number;
  inProgress?: number;
  pending?: number;
  description: string;
}
